import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { ShiftCardWrapper } from "@src/appV2/redesign/Shift/Card/Wrapper";
import { BookingCardContentWrapper } from "@src/appV2/redesign/Shift/CardContentWrapper";
import { InstantPayPill } from "@src/appV2/redesign/Shift/InstantPayPill";
import { ShiftCardPillsWrapper } from "@src/appV2/redesign/Shift/PillsWrapper";
import { BookingCardSummaryWrapper } from "@src/appV2/redesign/Shift/SummaryWrapper";
import { type ModalShiftBreakPolicy } from "@src/appV2/redesign/Shift/types";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";

import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";

interface ShiftBlockShiftCardProps {
  shift: {
    id: string;
    start: string;
    end: string;
    time: number;
    pay: number;
    isInstantPay: boolean;
    offer?: { pay?: { value: number } } | null;
    breakPolicy?: ModalShiftBreakPolicy;
  };
  agentTimezone: string;
}

export function ShiftBlockShiftCard(props: ShiftBlockShiftCardProps) {
  const { shift, agentTimezone } = props;

  const formattedHourlyPay = formatDollarsAsUsd(shift.offer?.pay?.value ?? shift.pay, {
    minimumFractionDigits: 2,
  });

  const breakDurationInMinutes = shift.breakPolicy?.breakDurationInMinutes ?? 0;
  const requiresBreak = isDefined(shift.breakPolicy);

  return (
    <ShiftCardWrapper sx={{ minHeight: "auto" }}>
      <BookingCardContentWrapper sx={{ gap: 2 }}>
        <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
          <ShiftCardPillsWrapper>{shift.isInstantPay && <InstantPayPill />}</ShiftCardPillsWrapper>

          <HourlyPayLabel hourlyPay={formattedHourlyPay} size="small" />
        </BookingCardSummaryWrapper>

        <Stack gap={1}>
          <Text variant="body2">
            {formatShortDateWithWeekday(parseISO(shift.start), { timeZone: agentTimezone })}
          </Text>

          <Stack gap={0.5}>
            <Text semibold variant="body1">
              {formatTime(parseISO(shift.start), { timeZone: agentTimezone })} -{" "}
              {formatTime(parseISO(shift.end), { timeZone: agentTimezone })}
            </Text>
            <DurationWithBreakDisplay
              totalDurationInHours={shift.time}
              breakDurationInMinutes={breakDurationInMinutes}
              requiresBreak={requiresBreak}
            />
          </Stack>
        </Stack>
      </BookingCardContentWrapper>
    </ShiftCardWrapper>
  );
}
